import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserPlus, Shield, FileText, Bell, CheckCircle, AlertCircle } from 'lucide-react';
import BreathtakingCard from './shared/BreathtakingCard.js';

interface LPOnboardingData {
  personalInfo: {
    name: string;
    email: string;
    organization: string;
    investmentAmount: string;
  };
  preferences: {
    reportFrequency: 'daily' | 'weekly' | 'monthly';
    alertTypes: string[];
    dataAccess: 'basic' | 'enhanced';
  };
  compliance: {
    accreditedInvestor: boolean;
    riskTolerance: 'conservative' | 'moderate' | 'aggressive';
    investmentHorizon: string;
  };
}

interface LPOnboardingPanelProps {
  onComplete?: (data: LPOnboardingData) => void;
}

const LPOnboardingPanel: React.FC<LPOnboardingPanelProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<LPOnboardingData>({
    personalInfo: {
      name: '',
      email: '',
      organization: '',
      investmentAmount: ''
    },
    preferences: {
      reportFrequency: 'monthly',
      alertTypes: [],
      dataAccess: 'basic'
    },
    compliance: {
      accreditedInvestor: false,
      riskTolerance: 'moderate',
      investmentHorizon: ''
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const availableAlertTypes = [
    'Performance Updates',
    'Risk Threshold Breaches',
    'Monthly Reports',
    'Compliance Notifications',
    'Market Commentary'
  ];

  const handleInputChange = (section: keyof LPOnboardingData, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleAlertTypeToggle = (alertType: string) => {
    setFormData(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        alertTypes: prev.preferences.alertTypes.includes(alertType)
          ? prev.preferences.alertTypes.filter(type => type !== alertType)
          : [...prev.preferences.alertTypes, alertType]
      }
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/arcoversight/lp/onboard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        onComplete?.(formData);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('LP onboarding failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.personalInfo.name && formData.personalInfo.email && formData.personalInfo.organization);
      case 2:
        return formData.preferences.alertTypes.length > 0;
      case 3:
        return !!(formData.compliance.investmentHorizon && formData.compliance.accreditedInvestor);
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.personalInfo.name}
                  onChange={(e) => handleInputChange('personalInfo', 'name', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email Address *</label>
                <input
                  type="email"
                  value={formData.personalInfo.email}
                  onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="Enter your email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Organization *</label>
                <input
                  type="text"
                  value={formData.personalInfo.organization}
                  onChange={(e) => handleInputChange('personalInfo', 'organization', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="Your organization or fund"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Investment Amount</label>
                <input
                  type="text"
                  value={formData.personalInfo.investmentAmount}
                  onChange={(e) => handleInputChange('personalInfo', 'investmentAmount', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="e.g., $1,000,000"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white mb-4">Communication Preferences</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Report Frequency</label>
                <select
                  value={formData.preferences.reportFrequency}
                  onChange={(e) => handleInputChange('preferences', 'reportFrequency', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Data Access Level</label>
                <select
                  value={formData.preferences.dataAccess}
                  onChange={(e) => handleInputChange('preferences', 'dataAccess', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
                >
                  <option value="basic">Basic (Performance &amp; Risk)</option>
                  <option value="enhanced">Enhanced (+ Attribution)</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-4">Alert Preferences *</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availableAlertTypes.map((alertType) => (
                  <div key={alertType} className="flex items-center">
                    <input
                      type="checkbox"
                      id={alertType}
                      checked={formData.preferences.alertTypes.includes(alertType)}
                      onChange={() => handleAlertTypeToggle(alertType)}
                      className="w-4 h-4 text-yellow-400 bg-slate-800 border-slate-600 rounded focus:ring-yellow-400 focus:ring-2"
                    />
                    <label htmlFor={alertType} className="ml-3 text-sm text-slate-300">
                      {alertType}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white mb-4">Compliance &amp; Risk Profile</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Risk Tolerance</label>
                <select
                  value={formData.compliance.riskTolerance}
                  onChange={(e) => handleInputChange('compliance', 'riskTolerance', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
                >
                  <option value="conservative">Conservative</option>
                  <option value="moderate">Moderate</option>
                  <option value="aggressive">Aggressive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Investment Horizon *</label>
                <input
                  type="text"
                  value={formData.compliance.investmentHorizon}
                  onChange={(e) => handleInputChange('compliance', 'investmentHorizon', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="e.g., 3-5 years"
                />
              </div>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="accreditedInvestor"
                checked={formData.compliance.accreditedInvestor}
                onChange={(e) => handleInputChange('compliance', 'accreditedInvestor', e.target.checked)}
                className="w-4 h-4 text-yellow-400 bg-slate-800 border-slate-600 rounded focus:ring-yellow-400 focus:ring-2"
              />
              <label htmlFor="accreditedInvestor" className="ml-3 text-sm text-slate-300">
                I confirm that I am an accredited investor as defined by SEC regulations *
              </label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="space-y-6 p-6 min-h-screen">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center justify-center h-64"
        >
          <CheckCircle className="w-16 h-16 text-green-400 mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Welcome to arcOversight!</h2>
          <p className="text-slate-300 text-center max-w-md">
            Your LP account has been successfully created. You will receive access credentials via email within 24 hours.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between"
      >
        <h1 className="text-3xl font-bold text-yellow-400">LP Onboarding</h1>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
          <span className="text-sm text-slate-300">Step {currentStep} of 3</span>
        </div>
      </motion.div>

      <div className="flex items-center justify-center mb-8">
        <div className="flex items-center space-x-4">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step <= currentStep 
                  ? 'bg-yellow-400 text-slate-900' 
                  : 'bg-slate-700 text-slate-400'
              }`}>
                {step}
              </div>
              {step < 3 && (
                <div className={`w-12 h-0.5 mx-2 ${
                  step < currentStep ? 'bg-yellow-400' : 'bg-slate-700'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      <BreathtakingCard
        title="Limited Partner Registration"
        variant="performance"
        size="lg"
        className="max-w-4xl mx-auto"
      >
        {renderStepContent()}

        <div className="flex justify-between mt-8 pt-6 border-t border-slate-700">
          <button
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className="px-6 py-3 bg-slate-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
          >
            Previous
          </button>

          {currentStep < 3 ? (
            <button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!isStepValid(currentStep)}
              className="px-6 py-3 bg-yellow-500 text-slate-900 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-yellow-400 transition-colors"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!isStepValid(currentStep) || isSubmitting}
              className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-500 transition-colors flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Submitting...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Complete Registration
                </>
              )}
            </button>
          )}
        </div>

        {submitStatus === 'error' && (
          <div className="mt-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">Registration failed. Please try again or contact support.</span>
          </div>
        )}
      </BreathtakingCard>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto"
      >
        <BreathtakingCard
          title="IP Protection"
          variant="risk"
          size="sm"
          icon={<Shield className="w-5 h-5 text-green-400" />}
        >
          <p className="text-xs text-slate-400">
            All strategy details are anonymized and protected. Only performance attribution is visible.
          </p>
        </BreathtakingCard>

        <BreathtakingCard
          title="Certified Reports"
          variant="performance"
          size="sm"
          icon={<FileText className="w-5 h-5 text-blue-400" />}
        >
          <p className="text-xs text-slate-400">
            Monthly certified performance reports with third-party audit verification.
          </p>
        </BreathtakingCard>

        <BreathtakingCard
          title="Real-time Alerts"
          variant="comparison"
          size="sm"
          icon={<Bell className="w-5 h-5 text-purple-400" />}
        >
          <p className="text-xs text-slate-400">
            Customizable notifications for performance thresholds and risk events.
          </p>
        </BreathtakingCard>
      </motion.div>
    </div>
  );
};

export default LPOnboardingPanel;
